import { useState, useEffect, useCallback } from "react";
import {
  View,
  Text,
  TouchableOpacity,
  TextInput,
  ScrollView,
  Modal,
  Switch,
  KeyboardAvoidingView,
  Platform,
  Alert,
  ActivityIndicator,
} from "react-native";
import { SafeAreaView } from "react-native-safe-area-context";
import { useLocalSearchParams, useRouter } from "expo-router";
import { Ionicons } from "@expo/vector-icons";
import { useTheme, useThemeColors } from "@/providers/ThemeProvider";
import { useAuth } from "@/providers/AuthProvider";
import { supabase } from "@/lib/supabase/client";
import { logActivity } from "@/lib/activity-log";
import { AgendaEvent, AgendaCategory } from "@/types";
import { format, addHours, parseISO } from "date-fns";
import { nl } from "date-fns/locale";
import DateTimePicker from "@react-native-community/datetimepicker";
import LottieView from "lottie-react-native";
import { LocationPicker } from "@/modules/agenda/components/LocationPicker";
import { getAllLotties, getLottieSource } from "@/lib/lottie-registry";

/**
 * Category options
 */
const CATEGORY_OPTIONS: { value: AgendaCategory; label: string; icon: string }[] = [
  { value: "activity", label: "Activiteit", icon: "game-controller" },
  { value: "meal", label: "Maaltijd", icon: "restaurant" },
  { value: "game", label: "Spel", icon: "trophy" },
  { value: "ceremony", label: "Ceremonie", icon: "ribbon" },
  { value: "free_time", label: "Vrije tijd", icon: "cafe" },
  { value: "custom", label: "Anders", icon: "ellipse" },
];

/**
 * Admin-only categories
 */
const ADMIN_CATEGORY_OPTIONS: { value: AgendaCategory; label: string; icon: string }[] = [
  { value: "family_day", label: "Familie Dag", icon: "sunny" },
  { value: "family_weekend", label: "Familie Weekend", icon: "calendar" },
  ...CATEGORY_OPTIONS,
];

/**
 * Color options
 */
const COLOR_OPTIONS = [
  "#7C3AED", "#3B82F6", "#10B981", "#F59E0B",
  "#EF4444", "#EC4899", "#6366F1", "#6B7280",
];

/**
 * Lottie animation options - uses central registry
 */
const LOTTIE_OPTIONS: { value: string | null; label: string; emoji: string }[] = [
  { value: null, label: "Geen", emoji: "❌" },
  ...getAllLotties()
    .filter((l) => ["celebration", "action", "object"].includes(l.category))
    .map((l) => ({
      value: l.key,
      label: l.name,
      emoji: "🎬",
    })),
];

/**
 * Edit Event Screen
 */
export default function EditEventScreen() {
  const { id } = useLocalSearchParams<{ id: string }>();
  const router = useRouter();
  const { isDark } = useTheme();
  const colors = useThemeColors();
  const { profile, roles, activeWeekend } = useAuth();

  // Loading states
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [originalEvent, setOriginalEvent] = useState<AgendaEvent | null>(null);

  // Form state
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [location, setLocation] = useState("");
  const [locationCoordinates, setLocationCoordinates] = useState<{ latitude: number; longitude: number } | undefined>();
  const [category, setCategory] = useState<AgendaCategory>("activity");
  const [color, setColor] = useState(COLOR_OPTIONS[0]);
  const [startDate, setStartDate] = useState(new Date());
  const [endDate, setEndDate] = useState(addHours(new Date(), 1));
  const [allDay, setAllDay] = useState(false);
  const [isMandatory, setIsMandatory] = useState(false);
  const [sendReminder, setSendReminder] = useState(false);
  const [lottieAnimation, setLottieAnimation] = useState<string | null>(null);
  const [hasReveal, setHasReveal] = useState(false);
  const [revealTarget, setRevealTarget] = useState<"location" | "details" | "event" | "all">("location");
  const [secretTitle, setSecretTitle] = useState("");
  const [secretDescription, setSecretDescription] = useState("");
  const [revealAt, setRevealAt] = useState<Date | null>(null);

  // Date picker state
  const [showStartPicker, setShowStartPicker] = useState(false);
  const [showEndPicker, setShowEndPicker] = useState(false);
  const [showRevealDatePicker, setShowRevealDatePicker] = useState(false);
  const [pickerMode, setPickerMode] = useState<"date" | "time">("date");

  const isAdmin = roles.isFamilieAdmin || roles.isSuperAdmin;
  const isSuperAdmin = roles.isSuperAdmin;

  /**
   * Check if user can edit this event
   */
  const canEdit = useCallback(
    (event: AgendaEvent): boolean => {
      if (!profile?.id) return false;

      // Super admins can edit all events
      if (isSuperAdmin) return true;

      // Familie admins can edit all family/weekend events
      if (roles.isFamilieAdmin) {
        if (event.event_type === "family" || event.event_type === "weekend") {
          return true;
        }
      }

      // Event creator can edit their own events
      return event.created_by === profile.id;
    },
    [profile?.id, isSuperAdmin, roles.isFamilieAdmin]
  );

  /**
   * Fetch event data
   */
  const fetchEvent = useCallback(async () => {
    if (!id) return;

    setIsLoading(true);
    setError(null);

    try {
      const { data, error: fetchError } = await supabase
        .from("agenda_events")
        .select("*")
        .eq("id", id)
        .single();

      if (fetchError) throw fetchError;

      const event = data as AgendaEvent;

      // Check permissions
      if (!canEdit(event)) {
        setError("Je hebt geen rechten om dit evenement te bewerken");
        return;
      }

      // Populate form
      setOriginalEvent(event);
      setTitle(event.title);
      setDescription(event.description || "");
      setLocation(event.location || "");
      // Set coordinates if available
      if (event.location_lat && event.location_lng) {
        setLocationCoordinates({
          latitude: event.location_lat,
          longitude: event.location_lng,
        });
      }
      setHasReveal(event.has_reveal || false);
      setRevealTarget((event.reveal_target as typeof revealTarget) || "location");
      setSecretTitle(event.secret_title || "");
      setSecretDescription(event.secret_description || "");
      setRevealAt(event.reveal_at ? parseISO(event.reveal_at) : null);
      setCategory((event.category as AgendaCategory) || "activity");
      setColor(event.color || COLOR_OPTIONS[0]);
      setStartDate(parseISO(event.starts_at));
      setEndDate(event.ends_at ? parseISO(event.ends_at) : addHours(parseISO(event.starts_at), 1));
      setAllDay(event.all_day);
      setIsMandatory(event.is_mandatory);
      setSendReminder(event.send_reminder);
      setLottieAnimation(event.lottie_animation);
    } catch (err: any) {
      console.error("Error fetching event:", err);
      setError(err.message || "Kon evenement niet laden");
    } finally {
      setIsLoading(false);
    }
  }, [id, canEdit]);

  useEffect(() => {
    fetchEvent();
  }, [fetchEvent]);

  /**
   * Handle date change
   */
  const handleStartDateChange = useCallback((event: any, date?: Date) => {
    if (Platform.OS === "android") setShowStartPicker(false);
    if (date) {
      setStartDate(date);
      if (date >= endDate) {
        setEndDate(addHours(date, 1));
      }
    }
  }, [endDate]);

  const handleEndDateChange = useCallback((event: any, date?: Date) => {
    if (Platform.OS === "android") setShowEndPicker(false);
    if (date) setEndDate(date);
  }, []);

  const openStartPicker = (mode: "date" | "time") => {
    setPickerMode(mode);
    setShowStartPicker(true);
  };

  const openEndPicker = (mode: "date" | "time") => {
    setPickerMode(mode);
    setShowEndPicker(true);
  };

  /**
   * Get changes for activity log
   */
  const getChanges = useCallback(() => {
    if (!originalEvent) return {};

    const changes: Record<string, { from: any; to: any }> = {};

    if (title !== originalEvent.title) {
      changes.title = { from: originalEvent.title, to: title };
    }
    if (description !== (originalEvent.description || "")) {
      changes.description = { from: originalEvent.description, to: description };
    }
    if (location !== (originalEvent.location || "")) {
      changes.location = { from: originalEvent.location, to: location };
    }
    if (category !== originalEvent.category) {
      changes.category = { from: originalEvent.category, to: category };
    }
    if (color !== originalEvent.color) {
      changes.color = { from: originalEvent.color, to: color };
    }
    if (startDate.toISOString() !== originalEvent.starts_at) {
      changes.starts_at = { from: originalEvent.starts_at, to: startDate.toISOString() };
    }
    if (allDay !== originalEvent.all_day) {
      changes.all_day = { from: originalEvent.all_day, to: allDay };
    }
    if (isMandatory !== originalEvent.is_mandatory) {
      changes.is_mandatory = { from: originalEvent.is_mandatory, to: isMandatory };
    }
    if (lottieAnimation !== originalEvent.lottie_animation) {
      changes.lottie_animation = { from: originalEvent.lottie_animation, to: lottieAnimation };
    }
    if (hasReveal !== originalEvent.has_reveal) {
      changes.has_reveal = { from: originalEvent.has_reveal, to: hasReveal };
    }

    return changes;
  }, [originalEvent, title, description, location, category, color, startDate, allDay, isMandatory, lottieAnimation, hasReveal]);

  /**
   * Submit changes
   */
  const handleSubmit = useCallback(async () => {
    if (!title.trim()) {
      Alert.alert("Fout", "Vul een titel in");
      return;
    }

    if (!profile?.id || !originalEvent) {
      Alert.alert("Fout", "Kon wijzigingen niet opslaan");
      return;
    }

    setIsSubmitting(true);

    try {
      const updateData = {
        title: title.trim(),
        description: description.trim() || null,
        location: location.trim() || null,
        location_lat: locationCoordinates?.latitude || null,
        location_lng: locationCoordinates?.longitude || null,
        starts_at: startDate.toISOString(),
        ends_at: allDay ? null : endDate.toISOString(),
        all_day: allDay,
        category,
        color,
        is_mandatory: isMandatory,
        send_reminder: sendReminder,
        lottie_animation: lottieAnimation,
        has_reveal: hasReveal,
        is_revealed: !hasReveal ? true : originalEvent?.is_revealed,
        reveal_target: hasReveal ? revealTarget : null,
        reveal_at: hasReveal && revealAt ? revealAt.toISOString() : null,
        secret_title: hasReveal && secretTitle.trim() ? secretTitle.trim() : null,
        secret_description: hasReveal && secretDescription.trim() ? secretDescription.trim() : null,
        updated_at: new Date().toISOString(),
      };

      const { error: updateError } = await supabase
        .from("agenda_events")
        .update(updateData)
        .eq("id", id);

      if (updateError) throw updateError;

      // Log the activity
      const changes = getChanges();
      await logActivity({
        user_id: profile.id,
        action_type: "agenda.update",
        action_category: "agenda",
        target_type: "agenda_event",
        target_id: id,
        target_name: title,
        metadata: {
          changes,
          event_type: originalEvent.event_type,
          edited_by_role: isSuperAdmin ? "super_admin" : roles.isFamilieAdmin ? "familie_admin" : "user",
        },
        weekend_id: activeWeekend?.id,
      });

      Alert.alert("Succes", "Evenement bijgewerkt!", [
        { text: "OK", onPress: () => router.back() },
      ]);
    } catch (err: any) {
      console.error("Error updating event:", err);
      Alert.alert("Fout", err.message || "Kon evenement niet bijwerken");
    } finally {
      setIsSubmitting(false);
    }
  }, [
    title, description, location, startDate, endDate, allDay, category, color,
    isMandatory, sendReminder, lottieAnimation, id, profile, originalEvent,
    getChanges, isSuperAdmin, roles.isFamilieAdmin, activeWeekend, router,
  ]);

  const categoryOptions = isAdmin ? ADMIN_CATEGORY_OPTIONS : CATEGORY_OPTIONS;

  if (isLoading) {
    return (
      <SafeAreaView className="flex-1 items-center justify-center" style={{ backgroundColor: colors.background }}>
        <ActivityIndicator size="large" color={colors.primary} />
      </SafeAreaView>
    );
  }

  if (error) {
    return (
      <SafeAreaView className="flex-1" style={{ backgroundColor: colors.background }}>
        <View className="flex-row items-center px-4 py-3">
          <TouchableOpacity onPress={() => router.back()}>
            <Ionicons name="arrow-back" size={24} color={colors.text} />
          </TouchableOpacity>
        </View>
        <View className="flex-1 items-center justify-center px-6">
          <Ionicons name="alert-circle" size={48} color={colors.error} />
          <Text className="text-lg font-medium mt-4 text-center" style={{ color: colors.text }}>
            {error}
          </Text>
          <TouchableOpacity onPress={() => router.back()} className="mt-4">
            <Text style={{ color: colors.primary }}>Ga terug</Text>
          </TouchableOpacity>
        </View>
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView className="flex-1" style={{ backgroundColor: colors.background }}>
      <KeyboardAvoidingView
        behavior={Platform.OS === "ios" ? "padding" : "height"}
        className="flex-1"
      >
        {/* Header */}
        <View
          className="flex-row items-center justify-between px-4 py-3 border-b"
          style={{ borderColor: colors.border }}
        >
          <TouchableOpacity onPress={() => router.back()}>
            <Ionicons name="close" size={24} color={colors.text} />
          </TouchableOpacity>
          <Text className="text-lg font-bold" style={{ color: colors.text }}>
            Bewerk Evenement
          </Text>
          <TouchableOpacity onPress={handleSubmit} disabled={isSubmitting}>
            {isSubmitting ? (
              <ActivityIndicator size="small" color={colors.primary} />
            ) : (
              <Text className="font-semibold" style={{ color: colors.primary }}>
                Opslaan
              </Text>
            )}
          </TouchableOpacity>
        </View>

        <ScrollView className="flex-1 px-4" showsVerticalScrollIndicator={false}>
          {/* Title */}
          <View className="mt-4">
            <Text className="text-sm font-medium mb-2" style={{ color: colors.textSecondary }}>
              Titel *
            </Text>
            <TextInput
              value={title}
              onChangeText={setTitle}
              placeholder="Naam van het evenement"
              placeholderTextColor={colors.textTertiary}
              className="px-4 py-3 rounded-xl"
              style={{ backgroundColor: colors.surfaceSecondary, color: colors.text }}
            />
          </View>

          {/* Category */}
          <View className="mt-4">
            <Text className="text-sm font-medium mb-2" style={{ color: colors.textSecondary }}>
              Categorie
            </Text>
            <ScrollView horizontal showsHorizontalScrollIndicator={false}>
              <View className="flex-row">
                {categoryOptions.map((cat) => (
                  <TouchableOpacity
                    key={cat.value}
                    onPress={() => setCategory(cat.value)}
                    className="mr-2 px-4 py-2 rounded-full flex-row items-center"
                    style={{
                      backgroundColor: category === cat.value ? colors.primary : colors.surfaceSecondary,
                    }}
                  >
                    <Ionicons
                      name={cat.icon as any}
                      size={16}
                      color={category === cat.value ? "#FFFFFF" : colors.textSecondary}
                    />
                    <Text
                      className="ml-2 text-sm font-medium"
                      style={{ color: category === cat.value ? "#FFFFFF" : colors.text }}
                    >
                      {cat.label}
                    </Text>
                  </TouchableOpacity>
                ))}
              </View>
            </ScrollView>
          </View>

          {/* Color */}
          <View className="mt-4">
            <Text className="text-sm font-medium mb-2" style={{ color: colors.textSecondary }}>
              Kleur
            </Text>
            <View className="flex-row">
              {COLOR_OPTIONS.map((c) => (
                <TouchableOpacity
                  key={c}
                  onPress={() => setColor(c)}
                  className="w-10 h-10 rounded-full mr-2 items-center justify-center"
                  style={{
                    backgroundColor: c,
                    borderWidth: color === c ? 3 : 0,
                    borderColor: colors.text,
                  }}
                >
                  {color === c && <Ionicons name="checkmark" size={20} color="#FFFFFF" />}
                </TouchableOpacity>
              ))}
            </View>
          </View>

          {/* Animation */}
          <View className="mt-4">
            <Text className="text-sm font-medium mb-2" style={{ color: colors.textSecondary }}>
              Animatie
            </Text>
            <ScrollView horizontal showsHorizontalScrollIndicator={false}>
              <View className="flex-row">
                {LOTTIE_OPTIONS.map((lottie) => (
                  <TouchableOpacity
                    key={lottie.value || "none"}
                    onPress={() => setLottieAnimation(lottie.value)}
                    className="mr-2 px-4 py-2 rounded-full flex-row items-center"
                    style={{
                      backgroundColor: lottieAnimation === lottie.value ? colors.primary : colors.surfaceSecondary,
                    }}
                  >
                    <Text className="text-base mr-1">{lottie.emoji}</Text>
                    <Text
                      className="text-sm font-medium"
                      style={{ color: lottieAnimation === lottie.value ? "#FFFFFF" : colors.text }}
                    >
                      {lottie.label}
                    </Text>
                  </TouchableOpacity>
                ))}
              </View>
            </ScrollView>
            {lottieAnimation && getLottieSource(lottieAnimation) && (
              <View className="mt-4 rounded-xl items-center justify-center py-4" style={{ backgroundColor: colors.surfaceSecondary }}>
                <LottieView
                  source={getLottieSource(lottieAnimation)}
                  autoPlay
                  loop
                  style={{ width: 120, height: 120 }}
                />
              </View>
            )}
          </View>

          {/* All Day */}
          <View
            className="flex-row items-center justify-between mt-4 px-4 py-3 rounded-xl"
            style={{ backgroundColor: colors.surfaceSecondary }}
          >
            <View className="flex-row items-center">
              <Ionicons name="sunny" size={20} color={colors.primary} />
              <Text className="ml-3 font-medium" style={{ color: colors.text }}>
                Hele dag
              </Text>
            </View>
            <Switch
              value={allDay}
              onValueChange={setAllDay}
              trackColor={{ false: colors.border, true: colors.primary }}
              thumbColor="#FFFFFF"
            />
          </View>

          {/* Date & Time */}
          <View className="mt-4">
            <Text className="text-sm font-medium mb-2" style={{ color: colors.textSecondary }}>
              Datum & Tijd
            </Text>
            <View
              className="flex-row items-center mb-2 px-4 py-3 rounded-xl"
              style={{ backgroundColor: colors.surfaceSecondary }}
            >
              <Ionicons name="play-circle" size={20} color={colors.success} />
              <Text className="ml-3 flex-1" style={{ color: colors.textSecondary }}>Start</Text>
              <TouchableOpacity
                onPress={() => openStartPicker("date")}
                className="px-3 py-1.5 rounded-lg mr-2"
                style={{ backgroundColor: colors.background }}
              >
                <Text style={{ color: colors.text }}>{format(startDate, "d MMM yyyy", { locale: nl })}</Text>
              </TouchableOpacity>
              {!allDay && (
                <TouchableOpacity
                  onPress={() => openStartPicker("time")}
                  className="px-3 py-1.5 rounded-lg"
                  style={{ backgroundColor: colors.background }}
                >
                  <Text style={{ color: colors.text }}>{format(startDate, "HH:mm")}</Text>
                </TouchableOpacity>
              )}
            </View>
            {!allDay && (
              <View
                className="flex-row items-center px-4 py-3 rounded-xl"
                style={{ backgroundColor: colors.surfaceSecondary }}
              >
                <Ionicons name="stop-circle" size={20} color={colors.error} />
                <Text className="ml-3 flex-1" style={{ color: colors.textSecondary }}>Einde</Text>
                <TouchableOpacity
                  onPress={() => openEndPicker("date")}
                  className="px-3 py-1.5 rounded-lg mr-2"
                  style={{ backgroundColor: colors.background }}
                >
                  <Text style={{ color: colors.text }}>{format(endDate, "d MMM yyyy", { locale: nl })}</Text>
                </TouchableOpacity>
                <TouchableOpacity
                  onPress={() => openEndPicker("time")}
                  className="px-3 py-1.5 rounded-lg"
                  style={{ backgroundColor: colors.background }}
                >
                  <Text style={{ color: colors.text }}>{format(endDate, "HH:mm")}</Text>
                </TouchableOpacity>
              </View>
            )}
          </View>

          {/* Location */}
          <View className="mt-4">
            <Text className="text-sm font-medium mb-2" style={{ color: colors.textSecondary }}>
              Locatie
            </Text>
            <LocationPicker
              value={location}
              onChange={(newLocation, coordinates) => {
                setLocation(newLocation);
                setLocationCoordinates(coordinates);
              }}
              placeholder="Voeg locatie toe"
            />
          </View>

          {/* Description */}
          <View className="mt-4">
            <Text className="text-sm font-medium mb-2" style={{ color: colors.textSecondary }}>
              Beschrijving
            </Text>
            <TextInput
              value={description}
              onChangeText={setDescription}
              placeholder="Voeg beschrijving toe"
              placeholderTextColor={colors.textTertiary}
              multiline
              numberOfLines={4}
              className="px-4 py-3 rounded-xl"
              style={{
                backgroundColor: colors.surfaceSecondary,
                color: colors.text,
                minHeight: 100,
                textAlignVertical: "top",
              }}
            />
          </View>

          {/* Admin options */}
          {isAdmin && (
            <>
              <View
                className="flex-row items-center justify-between mt-4 px-4 py-3 rounded-xl"
                style={{ backgroundColor: colors.surfaceSecondary }}
              >
                <View className="flex-row items-center">
                  <Ionicons name="alert-circle" size={20} color={colors.warning} />
                  <Text className="ml-3 font-medium" style={{ color: colors.text }}>
                    Verplicht evenement
                  </Text>
                </View>
                <Switch
                  value={isMandatory}
                  onValueChange={setIsMandatory}
                  trackColor={{ false: colors.border, true: colors.warning }}
                  thumbColor="#FFFFFF"
                />
              </View>

              {/* Reveal Toggle */}
              <View
                className="flex-row items-center justify-between mt-4 px-4 py-3 rounded-xl"
                style={{
                  backgroundColor: colors.surfaceSecondary,
                  borderWidth: hasReveal ? 1 : 0,
                  borderColor: colors.primary,
                }}
              >
                <View className="flex-row items-center flex-1">
                  <Ionicons name="eye-off" size={20} color={colors.primary} />
                  <View className="ml-3 flex-1">
                    <Text className="font-medium" style={{ color: colors.text }}>
                      Onthulling activeren
                    </Text>
                    <Text className="text-xs" style={{ color: colors.textSecondary }}>
                      Verberg details tot onthulling
                    </Text>
                  </View>
                </View>
                <Switch
                  value={hasReveal}
                  onValueChange={setHasReveal}
                  trackColor={{ false: colors.border, true: colors.primary }}
                  thumbColor="#FFFFFF"
                />
              </View>

              {hasReveal && (
                <View className="mt-4">
                  {/* What to hide */}
                  <Text className="text-sm font-medium mb-2" style={{ color: colors.textSecondary }}>
                    Wat wil je verbergen?
                  </Text>
                  <View className="flex-row flex-wrap mb-4">
                    {[
                      { value: "location", label: "Locatie", icon: "location" },
                      { value: "details", label: "Beschrijving", icon: "document-text" },
                      { value: "event", label: "Titel", icon: "text" },
                      { value: "all", label: "Alles", icon: "eye-off" },
                    ].map((option) => (
                      <TouchableOpacity
                        key={option.value}
                        onPress={() => setRevealTarget(option.value as typeof revealTarget)}
                        className="mr-2 mb-2 px-4 py-2 rounded-full flex-row items-center"
                        style={{
                          backgroundColor: revealTarget === option.value ? colors.primary : colors.surfaceSecondary,
                        }}
                      >
                        <Ionicons
                          name={option.icon as any}
                          size={16}
                          color={revealTarget === option.value ? "#FFFFFF" : colors.textSecondary}
                        />
                        <Text
                          className="ml-2 text-sm font-medium"
                          style={{ color: revealTarget === option.value ? "#FFFFFF" : colors.text }}
                        >
                          {option.label}
                        </Text>
                      </TouchableOpacity>
                    ))}
                  </View>

                  {/* Secret title (when hiding title) */}
                  {(revealTarget === "event" || revealTarget === "all") && (
                    <View className="mb-4">
                      <Text className="text-sm font-medium mb-2" style={{ color: colors.textSecondary }}>
                        Vervangende titel (zichtbaar voor anderen)
                      </Text>
                      <TextInput
                        value={secretTitle}
                        onChangeText={setSecretTitle}
                        placeholder="Bijv. 'Geheime verrassing...'"
                        placeholderTextColor={colors.textTertiary}
                        className="px-4 py-3 rounded-xl"
                        style={{ backgroundColor: colors.surfaceSecondary, color: colors.text }}
                      />
                    </View>
                  )}

                  {/* Secret description (when hiding details) */}
                  {(revealTarget === "details" || revealTarget === "all") && (
                    <View className="mb-4">
                      <Text className="text-sm font-medium mb-2" style={{ color: colors.textSecondary }}>
                        Vervangende beschrijving
                      </Text>
                      <TextInput
                        value={secretDescription}
                        onChangeText={setSecretDescription}
                        placeholder="Bijv. 'Dit wordt later onthuld!'"
                        placeholderTextColor={colors.textTertiary}
                        className="px-4 py-3 rounded-xl"
                        style={{ backgroundColor: colors.surfaceSecondary, color: colors.text }}
                        multiline
                        numberOfLines={2}
                      />
                    </View>
                  )}

                  {/* Reveal date/time */}
                  <View className="mb-4">
                    <Text className="text-sm font-medium mb-2" style={{ color: colors.textSecondary }}>
                      Onthullingsdatum (optioneel)
                    </Text>
                    <TouchableOpacity
                      onPress={() => setShowRevealDatePicker(true)}
                      className="flex-row items-center px-4 py-3 rounded-xl"
                      style={{ backgroundColor: colors.surfaceSecondary }}
                    >
                      <Ionicons name="time" size={20} color={colors.primary} />
                      <Text className="ml-3 flex-1" style={{ color: revealAt ? colors.text : colors.textTertiary }}>
                        {revealAt ? format(revealAt, "d MMM yyyy HH:mm", { locale: nl }) : "Geen datum ingesteld"}
                      </Text>
                      {revealAt && (
                        <TouchableOpacity onPress={() => setRevealAt(null)}>
                          <Ionicons name="close-circle" size={20} color={colors.textSecondary} />
                        </TouchableOpacity>
                      )}
                    </TouchableOpacity>
                    <Text className="text-xs mt-1" style={{ color: colors.textTertiary }}>
                      Kies wanneer de onthulling automatisch plaatsvindt
                    </Text>
                  </View>

                  {/* Info box */}
                  <View
                    className="p-4 rounded-xl"
                    style={{ backgroundColor: colors.primaryLight }}
                  >
                    <Text className="text-sm font-medium" style={{ color: colors.primary }}>
                      💡 Gebruik het oog-icoon op de evenement pagina om hints en uitdagingen te beheren
                    </Text>
                  </View>
                </View>
              )}
            </>
          )}

          {/* Reminder */}
          <View
            className="flex-row items-center justify-between mt-4 px-4 py-3 rounded-xl"
            style={{ backgroundColor: colors.surfaceSecondary }}
          >
            <View className="flex-row items-center">
              <Ionicons name="notifications" size={20} color={colors.primary} />
              <Text className="ml-3 font-medium" style={{ color: colors.text }}>
                Herinnering sturen
              </Text>
            </View>
            <Switch
              value={sendReminder}
              onValueChange={setSendReminder}
              trackColor={{ false: colors.border, true: colors.primary }}
              thumbColor="#FFFFFF"
            />
          </View>

          <View className="h-8" />
        </ScrollView>

        {/* Date Pickers */}
        {Platform.OS === "ios" && showStartPicker && (
          <Modal transparent animationType="fade">
            <View className="flex-1 justify-end" style={{ backgroundColor: colors.overlay }}>
              <View className="rounded-t-2xl p-4" style={{ backgroundColor: colors.surface }}>
                <View className="flex-row justify-between mb-4">
                  <TouchableOpacity onPress={() => setShowStartPicker(false)}>
                    <Text style={{ color: colors.primary }}>Annuleer</Text>
                  </TouchableOpacity>
                  <TouchableOpacity onPress={() => setShowStartPicker(false)}>
                    <Text className="font-semibold" style={{ color: colors.primary }}>Klaar</Text>
                  </TouchableOpacity>
                </View>
                <DateTimePicker
                  value={startDate}
                  mode={pickerMode}
                  display="spinner"
                  onChange={handleStartDateChange}
                  locale="nl-NL"
                  is24Hour={true}
                />
              </View>
            </View>
          </Modal>
        )}

        {Platform.OS === "ios" && showEndPicker && (
          <Modal transparent animationType="fade">
            <View className="flex-1 justify-end" style={{ backgroundColor: colors.overlay }}>
              <View className="rounded-t-2xl p-4" style={{ backgroundColor: colors.surface }}>
                <View className="flex-row justify-between mb-4">
                  <TouchableOpacity onPress={() => setShowEndPicker(false)}>
                    <Text style={{ color: colors.primary }}>Annuleer</Text>
                  </TouchableOpacity>
                  <TouchableOpacity onPress={() => setShowEndPicker(false)}>
                    <Text className="font-semibold" style={{ color: colors.primary }}>Klaar</Text>
                  </TouchableOpacity>
                </View>
                <DateTimePicker
                  value={endDate}
                  mode={pickerMode}
                  display="spinner"
                  onChange={handleEndDateChange}
                  minimumDate={startDate}
                  locale="nl-NL"
                  is24Hour={true}
                />
              </View>
            </View>
          </Modal>
        )}

        {Platform.OS === "android" && showStartPicker && (
          <DateTimePicker
            value={startDate}
            mode={pickerMode}
            display="default"
            onChange={handleStartDateChange}
            is24Hour={true}
          />
        )}

        {Platform.OS === "android" && showEndPicker && (
          <DateTimePicker
            value={endDate}
            mode={pickerMode}
            display="default"
            onChange={handleEndDateChange}
            minimumDate={startDate}
            is24Hour={true}
          />
        )}

        {/* Reveal Date Picker (iOS) */}
        {Platform.OS === "ios" && showRevealDatePicker && (
          <Modal transparent animationType="fade">
            <View className="flex-1 justify-end" style={{ backgroundColor: colors.overlay }}>
              <View className="rounded-t-2xl p-4" style={{ backgroundColor: colors.surface }}>
                <View className="flex-row justify-between mb-4">
                  <TouchableOpacity onPress={() => setShowRevealDatePicker(false)}>
                    <Text style={{ color: colors.primary }}>Annuleer</Text>
                  </TouchableOpacity>
                  <TouchableOpacity onPress={() => setShowRevealDatePicker(false)}>
                    <Text className="font-semibold" style={{ color: colors.primary }}>Klaar</Text>
                  </TouchableOpacity>
                </View>
                <DateTimePicker
                  value={revealAt || new Date()}
                  mode="datetime"
                  display="spinner"
                  onChange={(event, date) => {
                    if (date) setRevealAt(date);
                  }}
                  minimumDate={new Date()}
                  locale="nl-NL"
                  is24Hour={true}
                />
              </View>
            </View>
          </Modal>
        )}

        {/* Reveal Date Picker (Android) */}
        {Platform.OS === "android" && showRevealDatePicker && (
          <DateTimePicker
            value={revealAt || new Date()}
            mode="datetime"
            display="default"
            onChange={(event, date) => {
              setShowRevealDatePicker(false);
              if (date) setRevealAt(date);
            }}
            minimumDate={new Date()}
            is24Hour={true}
          />
        )}
      </KeyboardAvoidingView>
    </SafeAreaView>
  );
}

